import { Entity, type Engine } from "../../../engine/Engine";
import {
  POINTER_GESTURE_MOVE,
  POINTER_GESTURE_PINCH_BEGIN,
  POINTER_GESTURE_PRESS,
  POINTER_GESTURE_RELEASE,
  type PointerGestureMoveDetail,
  type PointerGesturePressDetail,
  type PointerGestureReleaseDetail,
} from "../../../input/PointerGestureInterpreter";
import type { DesignerPointerInteraction } from "./DesignerPointerInteraction";
import type { ProductGizmoCapture } from "../transform/ProductTranslateGizmo";

/** Subscribes once to normalized gestures and forwards them to the active tool. */
export class DesignerInteractionRouter extends Entity {
  private _active: DesignerPointerInteraction | null = null;
  private _detach?: () => void;

  constructor(
    private readonly _pointerEvents: EventTarget,
    private readonly _gizmoCapture?: ProductGizmoCapture,
  ) {
    super();
  }

  setActiveInteraction(interaction: DesignerPointerInteraction | null): void {
    if (interaction === this._active) return;
    const previous = this._active;
    this._active = null;
    previous?.cancel();
    previous?.deactivate?.();
    this._active = interaction;
    interaction?.activate?.();
  }

  override onAdd(engine: Engine): void {
    super.onAdd(engine);
    this._attach();
  }

  override onRemove(_engine: Engine): void {
    this.setActiveInteraction(null);
    this._detach?.();
  }

  /** Gizmo tutamacı pointer'ı almışken select/measure jesti işlemez. */
  private _gizmoOwnsPointer(): boolean {
    return this._gizmoCapture?.active === true;
  }

  private _onPress = (event: Event): void => {
    if (this._gizmoOwnsPointer()) return;
    const detail = (event as CustomEvent<PointerGesturePressDetail>).detail;
    if (detail) this._active?.press(detail);
  };

  private _onMove = (event: Event): void => {
    if (this._gizmoOwnsPointer()) return;
    const detail = (event as CustomEvent<PointerGestureMoveDetail>).detail;
    if (detail) this._active?.move(detail);
  };

  private _onRelease = (event: Event): void => {
    if (this._gizmoOwnsPointer()) return;
    const detail = (event as CustomEvent<PointerGestureReleaseDetail>).detail;
    if (detail) this._active?.release(detail);
  };

  private _onCancel = (): void => {
    if (this._gizmoOwnsPointer()) return;
    this._active?.cancel();
  };

  private _attach(): void {
    const target = this._pointerEvents;
    target.addEventListener(POINTER_GESTURE_PRESS, this._onPress);
    target.addEventListener(POINTER_GESTURE_MOVE, this._onMove);
    target.addEventListener(POINTER_GESTURE_RELEASE, this._onRelease);
    target.addEventListener(POINTER_GESTURE_PINCH_BEGIN, this._onCancel);
    this._detach = () => {
      target.removeEventListener(POINTER_GESTURE_PRESS, this._onPress);
      target.removeEventListener(POINTER_GESTURE_MOVE, this._onMove);
      target.removeEventListener(POINTER_GESTURE_RELEASE, this._onRelease);
      target.removeEventListener(POINTER_GESTURE_PINCH_BEGIN, this._onCancel);
    };
  }
}
